import { FC, useEffect, useState } from 'react'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import type { Theme } from '@perk-ui/core'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  makeStyles,
  TextField,
  Typography,
} from '@perk-ui/core'
import { useFormik } from 'formik'
import { Eye, EyeOff /*, Mail */ } from 'react-feather'
import { useLocation, useRouteMatch } from 'react-router'
import * as yup from 'yup'

import SideBannerContainer from '../../../components/SideBannerContainer'
import { useAuth } from '../../../features/auth/AuthContext'
import { humanizeBodySite } from '../../../features/query-hooks/useBodySites'
import useCreateAccount from '../../../features/query-hooks/useCreateAccount'
import useImpactTokenValidation from '../../../features/query-hooks/useImpactTokenValidation'
import useNpiValidation from '../../../features/query-hooks/useNpiValidation'
import { Sponsor } from '../../../features/query-hooks/useProviders'
import { useHistory } from '../../../utils/useHistory'
import ContactSponsorContent from '../../Provider/components/ContactSponsorContent'
import FormContainer from '../../Provider/components/FormContainer'
import FormHeader from '../../Provider/components/FormHeader'
import ProviderBanner from '../../Provider/components/ProviderBanner'
import NoTokenPage from './NoTokenPage'

interface ProviderRegisterProps {}

// Document configuration for consent tracking
const DOCUMENT_CONFIG = {
  terms: {
    name: 'Terms of Use',
    version: '1.2025',
    url: 'https://databiologics.com/terms-of-use',
  },
  privacy: {
    name: 'Privacy Policy',
    version: '1.2025',
    url: 'https://databiologics.com/privacy-policy',
  },
  psa: {
    name: 'Services Agreement + Business Associate Agreement',
    version: '1.2025',
    url: 'https://www.databiologics.com/provider-services-agreement',
  },
}

// Consent form version for tracking
const CONSENT_FORM_VERSION = 'provider_onboarding_v1.2025'

interface ProviderRegisterFormValues {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  npi: string
  termsAccepted: boolean
  marketingAccepted: boolean
}

export interface EnableProviderRegisterProps {
  token: string
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(4),
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    padding: theme.spacing(4),
    width: '100%',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.up('md')]: {
      maxWidth: 600, // medium laptops
      padding: theme.spacing(4.5),
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 720, // 1440px desktop sweet spot
      padding: theme.spacing(5),
    },
  },
  inputField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#F8F9FA',
      marginTop: theme.spacing(1),
      '& fieldset': {
        borderColor: '#E1E4E8',
        '& legend': {
          visibility: 'hidden',
          maxWidth: '0.01px',
        },
      },
      '&:hover fieldset': {
        borderColor: '#C4C9CF',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#6B7280',
      marginBottom: theme.spacing(2),
      '&.MuiInputLabel-shrink': {
        backgroundColor: '#FFFFFF',
        padding: '0 8px',
        marginLeft: '-4px',
        marginBottom: 0,
      },
    },
  },
  passwordHelper: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '-8px',
    marginBottom: theme.spacing(2),
  },
  checkboxContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    '& .MuiFormControlLabel-root': {
      alignItems: 'center',
      marginBottom: theme.spacing(1.5),
      '& .MuiCheckbox-root': {
        padding: '4px 8px',
        color: '#C4C9CF',
        alignSelf: 'flex-start',
        marginTop: '-2px',
      },
      '& .MuiFormControlLabel-label': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#4A5568',
        marginLeft: theme.spacing(0.5),
        '& a': {
          color: theme.palette.primary.main,
          textDecoration: 'underline',
        },
      },
    },
  },
  submitButton: {
    width: '100%',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: theme.palette.primary.dark,
    color: '#FFFFFF',
    marginTop: theme.spacing(2),
    '& form .MuiButton-root': {
      borderRadius: '8px',
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
    '&:disabled': {
      backgroundColor: '#C4C9CF',
      color: '#FFFFFF',
    },
  },
  loginLink: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    fontSize: '14px',
    color: '#6B7280',
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  // TODO: Re-enable when email support feature is ready
  // emailIcon: {
  //   position: 'fixed',
  //   bottom: theme.spacing(4),
  //   right: theme.spacing(4),
  //   backgroundColor: '#4A7C83',
  //   color: '#FFFFFF',
  //   width: '56px',
  //   height: '56px',
  //   borderRadius: '50%',
  //   display: 'flex',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  //   cursor: 'pointer',
  //   transition: 'transform 0.2s',
  //   '&:hover': {
  //     transform: 'scale(1.05)',
  //     backgroundColor: '#3A6C73',
  //   },
  // },
}))

const ProviderRegister: FC<ProviderRegisterProps> = () => {
  const { signIn, signOut, isAuthenticated } = useAuth()
  const classes = useStyles()
  const { push } = useHistory()
  const addAccount = useCreateAccount()
  const validateNpi = useNpiValidation()
  const tokenValidation = useImpactTokenValidation()

  // dynamic url segment
  const match = useRouteMatch<EnableProviderRegisterProps>()
  const UrlToken = match.params.token

  // search params
  const search = useLocation().search
  const searchParams = new URLSearchParams(search)
  const searchParamToken = searchParams.get('token') || undefined

  const token = UrlToken || searchParamToken || ''

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [previousNpiValidated, setPreviousNpiValidated] = useState('')
  const [isPreviousNpiValid, setIsPreviousNpiValid] = useState(false)
  const [npiValidated, setNpiValidated] = useState(false)
  const [isValidatingNpi, setIsValidatingNpi] = useState(false)
  const [tokenSponsor, setTokenSponsor] = useState<Sponsor | null>(null)
  const [isTokenInvalid, setIsTokenInvalid] = useState(false)
  const [showContactSponsor, setShowContactSponsor] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const registerValidations = yup.object({
    email: yup
      .string()
      .email('Please enter a valid email address')
      .required('Email is required'),
    password: yup
      .string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/(?=.*[0-9])/, 'Password must have a number')
      .matches(/(?=.*[^A-Za-z0-9])/, 'Password must have a symbol'),
    confirmPassword: yup
      .string()
      .required('Please confirm your password')
      .oneOf([yup.ref('password')], 'Passwords must match'),
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    npi: yup
      .string()
      .min(10, 'NPI must be 10 digits')
      .max(10, 'NPI must be 10 digits')
      .required('NPI is required.')
      .test('valid-npi', 'Invalid NPI Number', (value = '') =>
        value && value !== previousNpiValidated
          ? NpiVerification(value)
          : isPreviousNpiValid,
      ),
    termsAccepted: yup
      .boolean()
      .oneOf(
        [true],
        'You must accept the Terms of Service, Privacy Policy, Services Agreement, and Business Associate Agreement',
      ),
    marketingAccepted: yup.boolean(),
  })

  const formik = useFormik<ProviderRegisterFormValues>({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      npi: '',
      firstName: '',
      lastName: '',
      termsAccepted: false,
      marketingAccepted: false,
    },
    validationSchema: registerValidations,
    onSubmit: (values) => {
      // Trim whitespace from all values
      Object.keys(values).forEach((key) => {
        const k = key as keyof ProviderRegisterFormValues
        if (typeof values[k] === 'string') {
          values[k] = (values[k] as string).trim() as never
        }
      })

      // Prepare marketing consent for additional_data
      let additionalData = {}
      if (values.marketingAccepted) {
        additionalData = {
          marketing_consent: [true],
        }
      }

      // Prepare consents array if terms are accepted
      const consentsArray = values.termsAccepted
        ? [
            `${DOCUMENT_CONFIG.terms.name} v${DOCUMENT_CONFIG.terms.version}`,
            `${DOCUMENT_CONFIG.privacy.name} v${DOCUMENT_CONFIG.privacy.version}`,
            `${DOCUMENT_CONFIG.psa.name} v${DOCUMENT_CONFIG.psa.version}`,
          ]
        : []

      return addAccount
        .mutateAsync({
          firstName: values.firstName,
          lastName: values.lastName,
          npi: values.npi,
          email: values.email,
          password: values.password,
          type: 'Provider', // Always Provider for this registration
          needsVerify: false, // Provider registration starts inactive
          is_admin: false, // Provider accounts are not admin
          activated: false, // Provider accounts start inactive
          additional_data: additionalData, // Marketing consent in additional_data
          sponsor_id: tokenSponsor?.id, // Associate with sponsor for seat tracking
          consents: consentsArray, // Include consents for creation during staff creation
          consent_form_version: CONSENT_FORM_VERSION, // Version for consent tracking
        })
        .catch((err) => {
          if (err.response?.data?.keratin) {
            setSubmitError(err.response.data.keratin.message)
            throw err
          }
          if (
            err.response?.data?.errors?.npi &&
            err.response.data.errors.npi[0] === 'has already been taken'
          ) {
            setSubmitError('NPI number already taken.')
            throw err
          }
          if (
            err.response?.data?.errors?.email &&
            err.response.data.errors.email.includes('has already been taken')
          ) {
            setSubmitError(
              'An account with this email already exists. Please use a different email or login instead.',
            )
            throw err
          }
          setSubmitError(
            'Failed to create account. Please check your information and try again.',
          )
          throw err
        })
        .then((response) => {
          // First sign in, then create consents
          return signIn(values.email, values.password).then(() => response)
        })
        .then((response) => {
          // Consent creation is now handled during staff creation in the backend
          return response
        })
        .then(() => {
          // Redirect to provider flow after successful login and consent creation
          push('/provider')
        })
    },
  })

  const NpiVerification = (npi: string) => {
    if (npi.length !== 10) {
      setNpiValidated(false)
      return false
    }

    setIsValidatingNpi(true)
    return validateNpi
      .mutateAsync({
        npi: npi,
      })
      .then((res) => {
        setPreviousNpiValidated(npi)
        if (res.resultCount > 0) {
          const responseProvider = res.results[0].basic
          formik.setFieldValue(
            'firstName',
            humanizeBodySite(responseProvider.firstName),
          )
          formik.setFieldValue(
            'lastName',
            humanizeBodySite(responseProvider.lastName),
          )
          setIsPreviousNpiValid(true)
          setNpiValidated(true) // Show the firstName/lastName fields
          setIsValidatingNpi(false)
          return true
        } else {
          formik.setFieldValue('firstName', '')
          formik.setFieldValue('lastName', '')
          setIsPreviousNpiValid(false)
          setNpiValidated(false) // Hide the firstName/lastName fields
          setIsValidatingNpi(false)
          return false
        }
      })
      .catch(() => {
        setIsPreviousNpiValid(false)
        setNpiValidated(false) // Hide the firstName/lastName fields on error
        setIsValidatingNpi(false)
        return false
      })
  }

  useEffect(() => {
    if (token) {
      tokenValidation
        .mutateAsync({ token })
        .then((res) => {
          const sponsor = res.sponsor
          const seatsAvailable =
            (sponsor.providersAvailable || 0) - (sponsor.providersUsed || 0)

          if (seatsAvailable <= 0) {
            // No seats available - show contact sponsor content
            setShowContactSponsor(true)
            return
          }

          setTokenSponsor(sponsor)
        })
        .catch(() => {
          setIsTokenInvalid(true)
        })
    }
  }, [token])

  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setShowLogoutModal(true)
    }
  }, [isAuthenticated])

  // Handle logout and redirect back to registration with token
  const handleLogout = () => {
    // Save the current URL before signing out
    const currentUrl = window.location.pathname + window.location.search

    // Call signOut but don't await it - let it run in background
    signOut()

    // Immediately redirect to force page reload with token intact
    // This happens before AuthContext can redirect to login
    window.location.href = currentUrl
  }

  // Handle cancel - redirect to home
  const handleCancelLogout = () => {
    setShowLogoutModal(false)
    push('/home')
  }

  // Show NoTokenPage if token is missing or invalid
  if (!token) {
    return <NoTokenPage reason="missing" />
  }

  if (isTokenInvalid) {
    return <NoTokenPage reason="invalid" />
  }

  // If no seats available, show contact sponsor content
  if (showContactSponsor) {
    return (
      <SideBannerContainer
        bannerWidth={5}
        bannerSide="left"
        banner={<ProviderBanner />}
      >
        <ContactSponsorContent />
      </SideBannerContainer>
    )
  }

  return (
    <>
      {/* Logout Modal for already authenticated users */}
      <Dialog open={showLogoutModal} onClose={() => {}} disableEscapeKeyDown>
        <DialogTitle>Already Logged In</DialogTitle>
        <DialogContent sx={{ padding: 3 }}>
          <Typography>
            You are currently logged into another account. Please log out to
            register a new provider account.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: 'center', padding: 2, paddingTop: 0 }}
        >
          <Button onClick={handleCancelLogout}>Cancel</Button>
          <Button onClick={handleLogout} color="primary" variant="contained">
            Log Out and Continue
          </Button>
        </DialogActions>
      </Dialog>

      <SideBannerContainer
        bannerWidth={5}
        bannerSide="left"
        banner={<ProviderBanner />}
      >
        <Box className={classes.root}>
          <Box className={classes.formCard}>
            <FormHeader
              title="Create your account"
              note="This signup flow is currently available to US-based providers only. We're working to support additional regions soon!"
            />

            {submitError && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {submitError}
              </Alert>
            )}

            <FormContainer>
              <form
                onSubmit={formik.handleSubmit}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  contain: 'layout',
                }}
              >
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  required
                  className={classes.inputField}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={formik.isSubmitting}
                  placeholder="Enter a valid email address"
                  InputLabelProps={{ shrink: undefined }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      required
                      className={classes.inputField}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                      }
                      helperText={
                        formik.touched.password && formik.errors.password
                      }
                      disabled={formik.isSubmitting}
                      placeholder="Enter password"
                      InputLabelProps={{ shrink: undefined }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <EyeOff /> : <Eye />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      required
                      className={classes.inputField}
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.confirmPassword &&
                        Boolean(formik.errors.confirmPassword)
                      }
                      helperText={
                        formik.touched.confirmPassword &&
                        formik.errors.confirmPassword
                      }
                      disabled={formik.isSubmitting}
                      placeholder="Confirm password"
                      InputLabelProps={{ shrink: undefined }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? <EyeOff /> : <Eye />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography className={classes.passwordHelper}>
                  Password must be at least 8 characters and include a symbol
                  and a number
                </Typography>

                <TextField
                  name="npi"
                  label="NPI"
                  type="text"
                  variant="outlined"
                  fullWidth
                  required
                  className={classes.inputField}
                  value={formik.values.npi}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.npi && Boolean(formik.errors.npi)}
                  helperText={formik.touched.npi && formik.errors.npi}
                  disabled={formik.isSubmitting}
                  placeholder="Enter your 10-digit NPI number"
                  InputLabelProps={{ shrink: undefined }}
                  InputProps={{
                    endAdornment: isValidatingNpi ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />

                {npiValidated && (
                  <Box style={{ display: 'flex', gap: '16px', width: '100%' }}>
                    <TextField
                      name="firstName"
                      label="First Name"
                      type="text"
                      variant="outlined"
                      fullWidth
                      required={npiValidated}
                      className={classes.inputField}
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.firstName &&
                        Boolean(formik.errors.firstName)
                      }
                      helperText={
                        formik.touched.firstName && formik.errors.firstName
                      }
                      disabled={true}
                      placeholder="First name"
                      InputLabelProps={{ shrink: undefined }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      name="lastName"
                      label="Last Name"
                      type="text"
                      variant="outlined"
                      fullWidth
                      required={npiValidated}
                      className={classes.inputField}
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.lastName &&
                        Boolean(formik.errors.lastName)
                      }
                      helperText={
                        formik.touched.lastName && formik.errors.lastName
                      }
                      disabled={true}
                      placeholder="Last name"
                      InputLabelProps={{ shrink: undefined }}
                      style={{ flex: 1 }}
                    />
                  </Box>
                )}

                <Box className={classes.checkboxContainer}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.termsAccepted}
                        onChange={formik.handleChange}
                        name="termsAccepted"
                        disabled={formik.isSubmitting}
                        color="primary"
                      />
                    }
                    label={
                      <>
                        I agree to the{' '}
                        <Link
                          href={DOCUMENT_CONFIG.terms.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {DOCUMENT_CONFIG.terms.name}
                        </Link>
                        ,{' '}
                        <Link
                          href={DOCUMENT_CONFIG.privacy.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {DOCUMENT_CONFIG.privacy.name}
                        </Link>
                        , and{' '}
                        <Link
                          href={DOCUMENT_CONFIG.psa.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {DOCUMENT_CONFIG.psa.name}
                        </Link>
                      </>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.marketingAccepted}
                        onChange={formik.handleChange}
                        name="marketingAccepted"
                        disabled={formik.isSubmitting}
                        color="primary"
                      />
                    }
                    label="I agree to receive marketing and promotional communications from DataBiologics"
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  className={classes.submitButton}
                  disabled={
                    !formik.isValid || formik.isSubmitting || !tokenSponsor
                  }
                  style={{
                    borderRadius: '8px',
                  }}
                >
                  {formik.isSubmitting
                    ? 'Creating Account...'
                    : 'Create Your Provider Account'}
                </Button>

                <Box className={classes.loginLink}>
                  Already have an account? <Link href="/login">Login</Link>
                </Box>
              </form>
            </FormContainer>
          </Box>

          {/* TODO: Re-enable when email support feature is ready */}
          {/* Email Support Icon */}
          {/* <Box className={classes.emailIcon}>
            <Mail size={24} />
          </Box> */}
        </Box>
      </SideBannerContainer>
    </>
  )
}

export default ProviderRegister
